/**
 * Button — the canonical action button used across the app.
 *
 * Variants:
 *   primary  — solid brand teal CTA (default)
 *   secondary — surface bg + border (less prominent action)
 *   ghost    — transparent, hover bg only (toolbar, dropdowns)
 *   danger   — solid danger (destructive)
 *   link     — looks like a link, fits in body text
 *
 * Sizes: xs / sm / md (default) / lg
 *
 * Other:
 *   loading      — shows a spinner, disables the button
 *   leadingIcon  — icon component placed before the label
 *   trailingIcon — icon component placed after the label
 *   fullWidth    — w-full
 *   as           — render as a different element ('a', Link, etc.) — accepts a component
 *
 * Logical-property spacing (ms/me) so RTL flips for free.
 */

import { forwardRef } from 'react'
import Spinner from './Spinner'

const VARIANTS = {
  primary:   'bg-brand text-white hover:bg-brand-hover active:bg-brand-hover focus-visible:ring-brand/40 disabled:bg-brand/60',
  secondary: 'bg-surface text-ink-primary border border-border hover:bg-muted hover:border-border-strong focus-visible:ring-brand/30 disabled:opacity-50',
  ghost:     'bg-transparent text-ink-primary hover:bg-muted focus-visible:ring-brand/30 disabled:opacity-50',
  danger:    'bg-danger text-white hover:bg-danger/90 focus-visible:ring-danger/40 disabled:bg-danger/60',
  link:      'bg-transparent text-brand hover:text-brand-hover underline-offset-4 hover:underline focus-visible:ring-brand/30 px-0 py-0 h-auto',
}

const SIZES = {
  xs: 'h-7  px-2.5 text-xs gap-1',
  sm: 'h-8  px-3   text-xs gap-1.5',
  md: 'h-10 px-4   text-sm gap-2',
  lg: 'h-12 px-5   text-base gap-2',
}

const Button = forwardRef(function Button(
  {
    variant = 'primary',
    size = 'md',
    type = 'button',
    loading = false,
    disabled = false,
    leadingIcon: Leading,
    trailingIcon: Trailing,
    fullWidth = false,
    as: Component = 'button',
    className = '',
    children,
    ...rest
  },
  ref
) {
  const variantCls = VARIANTS[variant] || VARIANTS.primary
  const sizeCls = variant === 'link' ? '' : (SIZES[size] || SIZES.md)
  const widthCls = fullWidth ? 'w-full' : ''
  const isDisabled = disabled || loading

  const elementProps =
    Component === 'button'
      ? { type, disabled: isDisabled }
      : { 'aria-disabled': isDisabled || undefined }

  return (
    <Component
      ref={ref}
      {...elementProps}
      className={`
        inline-flex items-center justify-center rounded-lg font-medium
        transition-colors select-none
        focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas
        disabled:cursor-not-allowed
        ${variantCls} ${sizeCls} ${widthCls} ${className}
      `}
      {...rest}
    >
      {loading && <Spinner size="sm" tone={variant === 'primary' || variant === 'danger' ? 'inverse' : 'current'} />}
      {!loading && Leading && <Leading className="w-4 h-4 flex-shrink-0" aria-hidden="true" />}
      {children}
      {!loading && Trailing && <Trailing className="w-4 h-4 flex-shrink-0" aria-hidden="true" />}
    </Component>
  )
})

export default Button
